import React, { useState, useRef, useEffect } from "react";
import { Modal, Button, Input, List } from "antd";
import { GoogleMap, Polygon, Marker } from "@react-google-maps/api";

const containerStyle = {
  width: "100%",
  height: "400px",
};

const MapWithPolygon = ({ isOpen, onClose, onSave, islat, isLong }) => {
  const [path, setPath] = useState([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentCenter, setCurrentCenter] = useState({
    lat: islat ? islat : 19.0859,
    lng: isLong ? isLong : 72.9076,
  });

  const [searchText, setSearchText] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const autocompleteServiceRef = useRef(null);
  const placesServiceRef = useRef(null);
  const debounceTimeoutRef = useRef(null);

  useEffect(() => {
    if (!autocompleteServiceRef.current && window.google) {
      autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
    }
  }, []);

  // Handle map clicks
  const handleMapClick = (e) => {
    if (isDrawing) {
      const newPoint = { lat: e.latLng.lat(), lng: e.latLng.lng() };
      setPath((prevPath) => [...prevPath, newPoint]);
      setCurrentCenter(newPoint);
    }
  };

  // Handle clear polygon
  const clearPolygon = () => {
    setPath([]);
    setCurrentCenter({
      lat: islat ? islat : 19.0859,
      lng: isLong ? isLong : 72.9076,
    });
  };

  // Search input change (debounced 3s)
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    if (value.length > 0) {
      debounceTimeoutRef.current = setTimeout(() => {
        if (autocompleteServiceRef.current) {
          autocompleteServiceRef.current.getPlacePredictions(
            { input: value },
            (predictions, status) => {
              if (
                status === window.google.maps.places.PlacesServiceStatus.OK &&
                predictions
              ) {
                setSuggestions(predictions);
              } else {
                setSuggestions([]);
              }
            }
          );
        }
      }, 1000); // 1s delay
    } else {
      setSuggestions([]);
    }
  };

  // Handle suggestion click → fetch location
  const handleSuggestionClick = (placeId) => {
    if (!placesServiceRef.current) {
      placesServiceRef.current = new window.google.maps.places.PlacesService(
        document.createElement("div")
      );
    }

    placesServiceRef.current.getDetails({ placeId }, (place, status) => {
      if (
        status === window.google.maps.places.PlacesServiceStatus.OK &&
        place.geometry &&
        place.geometry.location
      ) {
        const newPoint = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        };
        setPath((prevPath) => [...prevPath, newPoint]);
        setCurrentCenter(newPoint);
        setSearchText(place.formatted_address || "");
        setSuggestions([]);
      }
    });
  };

  return (
    <Modal
      title="Create Polygon"
      open={isOpen}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          onClick={() => {
            onSave(path); 
            setPath([]); 
            setCurrentCenter({
              lat: islat ? islat : 19.0859,
              lng: isLong ? isLong : 72.9076,
            });
            setSearchText(""); 
          }}
          disabled={path.length === 0}
        >
          Save Coordinates
        </Button>
      ]}
      width={1000}
    >
      {/* Search input */}
      <div style={{ marginBottom: "10px", position: "relative" }}>
        <Input
          placeholder="Search location..."
          value={searchText}
          onChange={handleSearchChange}
        />

        {suggestions.length > 0 && (
          <List
            bordered
            style={{
              position: "absolute",
              top: "42px",
              width: "100%",
              zIndex: 1000,
              background: "#fff",
              maxHeight: "200px",
              overflowY: "auto",
            }}
            dataSource={suggestions}
            renderItem={(item) => (
              <List.Item
                key={item.place_id}
                onClick={() => handleSuggestionClick(item.place_id)}
                style={{ cursor: "pointer" }}
              >
                {item.description}
              </List.Item>
            )}
          />
        )}
      </div>

      <div className="flex justify-end gap-3 mb-3">
        <Button
          type={isDrawing ? "default" : "primary"}
          onClick={() => setIsDrawing((prev) => !prev)}
        >
          {isDrawing ? "Stop Drawing" : "Select Coordinates"}
        </Button>
        <Button key="clear" onClick={clearPolygon}>
          Clear Coordinates
        </Button>
      </div>

      <GoogleMap
        mapContainerStyle={containerStyle}
        center={currentCenter}
        zoom={10}
        onClick={handleMapClick}
      >
        {/* Polygon */}
        <Polygon
          path={path}
          options={{
            fillColor: "rgba(63, 81, 181, 0.5)",
            strokeColor: "#3f51b5",
            strokeWeight: 2,
          }}
        />

        {/* Markers */}
        {path.map((point, index) => (
          <Marker key={index} position={point} />
        ))}
      </GoogleMap>

      {/* Show coordinates */}
      <div style={{ marginTop: "20px" }}>
        <h3 className="font-semibold">Selected Coordinates:</h3>
        {path.length > 0 ? (
          <ul>
            {path.map((point, index) => (
              <li key={index}>
                Latitude: {point.lat}, Longitude: {point.lng}
              </li>
            ))}
          </ul>
        ) : (
          <p>No coordinates selected.</p>
        )}
      </div>
    </Modal>
  );
};

export default MapWithPolygon;
